import type { EeapHistoryRow } from '../services/eeapService'

export { sortAdvanceRows, type DateSort, type LedgerSortField } from '../utils/advanceRowSort.ts'
import { compareAdvanceRowsChronological } from '../utils/advanceRowSort.ts'

export type RunningBalanceRow = {
  id: string
  date: string | null
  employeeAmount?: number
  establishedBalance?: number | null
  scheduled?: boolean
  employeeId?: string
  runningBalance?: number
}

export function hasEstablishedBalance(row: { establishedBalance?: number | null }) {
  return row.establishedBalance != null && Number.isFinite(Number(row.establishedBalance))
}

/**
 * Combined EEAP + Advance running balance after each row in time order.
 * An established balance on a row becomes the running total at that row;
 * later rows start from there. Posted rows otherwise start from $0;
 * scheduled rows continue from the posted ending (or `opening` when
 * there is no posted history).
 */
export function withCombinedRunningBalances(
  posted: EeapHistoryRow[],
  upcoming: EeapHistoryRow[],
  opening = 0,
): EeapHistoryRow[] {
  const chronological = [...posted, ...upcoming].sort(compareAdvanceRowsChronological)
  let running = posted.length > 0 ? 0 : opening
  return chronological.map((row) => {
    running = hasEstablishedBalance(row)
      ? Number(row.establishedBalance)
      : running + Number(row.employeeAmount || 0)
    return { ...row, runningBalance: running }
  })
}

/** Per-employee running balances, using established checkpoints when present. */
export function withEmployeeRunningBalances<T extends RunningBalanceRow>(
  rows: T[],
): Array<T & { runningBalance: number | undefined }> {
  const chronological = [...rows].sort(compareAdvanceRowsChronological)
  const runningByEmployee = new Map<string, number>()
  const byId = new Map<string, number>()
  for (const row of chronological) {
    const key = String(row.employeeId || '')
    const next = hasEstablishedBalance(row)
      ? Number(row.establishedBalance)
      : (runningByEmployee.get(key) || 0) + Number(row.employeeAmount || 0)
    runningByEmployee.set(key, next)
    byId.set(row.id, next)
  }
  return rows.map((row) => ({ ...row, runningBalance: byId.get(row.id) }))
}
